import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAutocomplete } from "@/hooks/useSearch";

const categoryColors: Record<string, string> = {
  "Success Story": "text-orange-500 bg-orange-50",
  "Startup": "text-green-600 bg-green-50",
  "Innovation": "text-yellow-600 bg-yellow-50",
  "Femme Entrepreneuse": "text-pink-500 bg-pink-50",
  "Jeune Talent": "text-purple-500 bg-purple-50",
  "Diaspora Africaine": "text-teal-600 bg-teal-50",
};

interface Props {
  onClose?: () => void;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
}

export default function GlobalSearchBar({ onClose, autoFocus = false, placeholder, className = "" }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { suggestions, loading } = useAutocomplete(query);

  const resolvedPlaceholder = placeholder ?? t("nav.searchPlaceholder");

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    setOpen(query.trim().length >= 2);
    setActiveIdx(-1);
  }, [query, suggestions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goToSearch = useCallback((q: string) => {
    if (!q.trim()) return;
    setOpen(false);
    setQuery("");
    navigate(`/recherche?q=${encodeURIComponent(q.trim())}`);
    onClose?.();
  }, [navigate, onClose]);

  const goToArticle = useCallback((id: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/article/${id}`);
    onClose?.();
  }, [navigate, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        goToArticle(suggestions[activeIdx].id);
      } else {
        goToSearch(query);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      onClose?.();
    }
  };

  const highlightMatch = (text: string, q: string) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-orange-100 text-orange-700 rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative flex items-center">
        <i className="ri-search-line absolute left-4 text-gray-400 text-base pointer-events-none"></i>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          placeholder={resolvedPlaceholder}
          className="w-full pl-11 pr-10 py-3 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setOpen(false); inputRef.current?.focus(); }}
            className="absolute right-4 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl overflow-hidden z-50" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          {loading ? (
            <div className="px-4 py-6 flex items-center justify-center gap-2 text-gray-400 text-sm">
              <i className="ri-loader-4-line animate-spin"></i>
              {t("search.loading")}
            </div>
          ) : suggestions.length > 0 ? (
            <>
              <div className="px-4 pt-3 pb-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t("search.suggestions")}</p>
              </div>
              {suggestions.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => goToArticle(s.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${activeIdx === idx ? "bg-orange-50" : "hover:bg-gray-50"}`}
                >
                  <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                      {highlightMatch(s.title, query)}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${categoryColors[s.category] ?? "text-gray-500 bg-gray-100"}`}>
                        {s.category}
                      </span>
                      <span className="text-xs text-gray-400">{s.countryFlag} {s.country}</span>
                    </div>
                  </div>
                  <i className="ri-arrow-right-up-line text-gray-300 flex-shrink-0"></i>
                </button>
              ))}
              {/* See all results */}
              <button
                onClick={() => goToSearch(query)}
                className={`w-full flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm font-semibold text-orange-500 hover:bg-orange-50 transition-colors cursor-pointer ${activeIdx === suggestions.length ? "bg-orange-50" : ""}`}
              >
                <span>{t("search.seeAll")} &quot;{query}&quot;</span>
                <i className="ri-search-line"></i>
              </button>
            </>
          ) : (
            <div className="px-4 py-6 text-center">
              <i className="ri-search-line text-3xl text-gray-200 block mb-2"></i>
              <p className="text-sm text-gray-500">{t("search.noResult")} &quot;{query}&quot;</p>
              <button
                onClick={() => goToSearch(query)}
                className="mt-3 text-xs text-orange-500 hover:underline cursor-pointer"
              >
                {t("search.searchAnyway")}
              </button>
            </div>
          )}

          {/* Quick categories */}
          {!loading && suggestions.length === 0 && (
            <div className="px-4 pb-4 pt-2 border-t border-gray-50">
              <p className="text-xs text-gray-400 mb-2">{t("search.browseCategory")}</p>
              <div className="flex flex-wrap gap-2">
                {["Success Story", "Startup", "Innovation", "Femme Entrepreneuse", "Jeune Talent", "Diaspora Africaine"].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { navigate(`/recherche?q=&category=${encodeURIComponent(cat)}`); setOpen(false); onClose?.(); }}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer transition-colors ${categoryColors[cat] ?? "text-gray-500 bg-gray-100"}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
